/**
 * Lifecycle glue: mic acquisition, live permission state, silence watching.
 *
 * Owns the things that must be torn down — the permission subscriber, the
 * media stream, the level monitor — so a visitor who navigates away does not
 * leave a hot mic behind.
 *
 * On permission state: `prompt` is the ordinary first-run state, not a
 * failure, and banners nothing. Neither does the subscriber's opening call,
 * whatever it reports: that call describes ambient state, not something that
 * just happened, and a visitor who has not asked for voice yet does not need
 * to be told about a permission they have not tried to use. A refusal they
 * do hit is caught by `requestMic` at click time. Only a genuine transition
 * to `denied` — the browser revoking mid-session, which is exactly what the
 * subscriber exists to catch — raises a banner on its own.
 */

import { useCallback, useEffect, useRef } from 'react';

import { detectVoiceCapability, subscribePermissionChange } from '@/voice/availability';
import { clearBannerKinds, MIC_OWNED_KINDS, showBanner } from '@/voice/bannerPrecedence';
import { makeMicPermissionDeniedBanner } from '@/voice/fallbacks/micPermissionDenied';
import { makeSilentStreamBanner } from '@/voice/fallbacks/silentStream';
import { applyUnsupported, requestMic, stopStream } from '@/voice/micGate';
import { createStreamSampler, startSilenceMonitor } from '@/voice/silenceMonitor';
import type { VoiceStoreSlice } from '@/voice/types';

export interface VoiceFallbackControls {
  readonly enableVoice: () => void;
  readonly disableVoice: () => void;
  /**
   * The live capture stream, or null when voice is off.
   *
   * Exposed as a getter rather than returned state because the recorder
   * that consumes it is started from a click handler, which needs the
   * stream as of now, not as of the render that installed the handler.
   */
  readonly getStream: () => MediaStream | null;
}

export function useVoiceFallbacks(store: VoiceStoreSlice): VoiceFallbackControls {
  const streamRef = useRef<MediaStream | null>(null);
  const stopMonitorRef = useRef<(() => void) | null>(null);
  const listeningRef = useRef(false);
  const storeRef = useRef(store);
  storeRef.current = store;

  const teardown = useCallback((): void => {
    listeningRef.current = false;
    stopMonitorRef.current?.();
    stopMonitorRef.current = null;
    stopStream(streamRef.current);
    streamRef.current = null;
  }, []);

  const enableVoice = useCallback((): void => {
    void (async (): Promise<void> => {
      const s = storeRef.current;
      const outcome = await requestMic(s, enableVoice);
      if (!outcome.ok) {
        teardown();
        return;
      }
      teardown();
      streamRef.current = outcome.stream;
      listeningRef.current = true;

      const sampler = createStreamSampler(outcome.stream);
      if (sampler === null) return;
      stopMonitorRef.current = startSilenceMonitor({
        sampler,
        isListening: () => listeningRef.current,
        onSilent: () => {
          showBanner(storeRef.current, makeSilentStreamBanner(enableVoice));
        },
        onSound: () => {
          clearBannerKinds(storeRef.current, ['silent-stream']);
        },
      });
    })();
  }, [teardown]);

  const disableVoice = useCallback((): void => {
    teardown();
    // Only the mic layer's own complaints; a cap banner outlives the toggle.
    clearBannerKinds(storeRef.current, MIC_OWNED_KINDS);
    storeRef.current.setVoiceMode('text-only');
  }, [teardown]);

  // Feature detection on arrival, so no dead mic button is ever offered.
  useEffect(() => {
    if (detectVoiceCapability() === 'unsupported') applyUnsupported(storeRef.current);
  }, []);

  // Live permission state, and the teardown for everything this hook owns.
  useEffect(() => {
    let seenFirst = false;
    const unsubscribe = subscribePermissionChange((state) => {
      const s = storeRef.current;
      if (!seenFirst) {
        seenFirst = true;
        return;
      }
      if (state === 'denied') {
        teardown();
        if (showBanner(s, makeMicPermissionDeniedBanner(enableVoice))) {
          s.setVoiceMode('fallen-back');
        }
        return;
      }
      // granted or prompt: the block is lifted, so retract the complaint.
      clearBannerKinds(s, ['mic-permission-denied']);
    });

    return (): void => {
      unsubscribe();
      teardown();
    };
  }, [teardown, enableVoice]);

  const getStream = useCallback((): MediaStream | null => streamRef.current, []);

  return { enableVoice, disableVoice, getStream };
}
